import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  UserPlus, Pencil, ShieldCheck, Ban, Pause, Play, Search, Eye,
  MailCheck, KeyRound, LogOut, Copy, MailWarning, History
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = { admin: 'Administrador', comercial: 'Comercial', tecnico: 'Técnico', cliente: 'Cliente', user: 'Cliente' };
const ROLE_STYLES = {
  admin: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  comercial: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  tecnico: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  cliente: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  user: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
};
const ESTADO_STYLES = {
  activo: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  suspendido: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  desactivado: 'bg-red-500/15 text-red-300 border-red-500/30',
};
const EVENTO_COLORS = {
  cuenta_creada: 'bg-sky-400', cuenta_activada: 'bg-emerald-400', correo_verificado: 'bg-emerald-400',
  primer_acceso: 'bg-sky-400', cambio_password: 'bg-amber-400', cambio_email: 'bg-amber-400',
  cambio_email_solicitado: 'bg-amber-400', cambio_rol: 'bg-purple-400', suspension: 'bg-red-400',
  desactivacion: 'bg-red-500', reactivacion: 'bg-emerald-400', sesiones_cerradas: 'bg-zinc-400',
  reset_solicitado: 'bg-zinc-400', email_verificacion_enviado: 'bg-zinc-400',
};

const copiar = async (texto) => {
  try {
    await navigator.clipboard.writeText(texto);
    toast.success('Enlace copiado al portapapeles');
  } catch {
    toast.error('No se pudo copiar. Selecciona y copia manualmente: ' + texto.slice(0, 60) + '...');
  }
};

function mostrarResultadoEnvio(data) {
  if (data.email_enviado) {
    toast.success(data.message);
  } else {
    toast.warning(`${data.message}. El correo NO se envió (${data.error || 'sin API key de Resend'}). Copia el enlace desde la ficha.`, { duration: 9000 });
  }
}

function UserDetailModal({ usuario, onClose, onChanged }) {
  const [eventos, setEventos] = useState(null);
  const [working, setWorking] = useState(false);

  const fetchSeguridad = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/users/${usuario.id}/seguridad`, { withCredentials: true });
      setEventos(r.data.eventos || []);
    } catch { setEventos([]); }
  }, [usuario.id]);

  useEffect(() => { fetchSeguridad(); }, [fetchSeguridad]);

  const accion = async (path, confirmMsg = null) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setWorking(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/users/${usuario.id}/${path}`, {}, { withCredentials: true });
      if (r.data.enlace !== undefined) mostrarResultadoEnvio(r.data);
      else toast.success(r.data.message);
      fetchSeguridad();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en la acción');
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="bg-[#0a0a0a] border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="user-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] font-light text-xl">Ficha de seguridad · {usuario.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <p className="text-[10px] text-zinc-500 mb-1">Estado</p>
              <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${ESTADO_STYLES[usuario.estado]}`}>{usuario.estado}</span>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <p className="text-[10px] text-zinc-500 mb-1">Correo</p>
              <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${usuario.email_verificado ? ESTADO_STYLES.activo : ESTADO_STYLES.suspendido}`} data-testid="detail-verificado">
                {usuario.email_verificado ? 'Verificado' : 'Sin verificar'}
              </span>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <p className="text-[10px] text-zinc-500 mb-1">Creación</p>
              <p className="text-xs text-white">{usuario.created_at?.slice(0, 10) || '—'}</p>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <p className="text-[10px] text-zinc-500 mb-1">Último acceso</p>
              <p className="text-xs text-white">{usuario.last_login ? usuario.last_login.slice(0, 16).replace('T', ' ') : 'Nunca'}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {!usuario.email_verificado && (
              <Button size="sm" variant="outline" disabled={working} onClick={() => accion('reenviar-verificacion')}
                className="border-sky-500/30 text-sky-400 h-8" data-testid="detail-reenviar-verificacion-btn">
                <MailCheck className="w-3.5 h-3.5 mr-1.5" />Reenviar verificación
              </Button>
            )}
            <Button size="sm" variant="outline" disabled={working} onClick={() => accion('reset-password-link')}
              className="border-amber-500/30 text-amber-400 h-8" data-testid="detail-reset-password-btn">
              <KeyRound className="w-3.5 h-3.5 mr-1.5" />Enviar restablecimiento de contraseña
            </Button>
            <Button size="sm" variant="outline" disabled={working}
              onClick={() => accion('cerrar-sesiones', `¿Cerrar TODAS las sesiones activas de ${usuario.name}? Tendrá que volver a iniciar sesión.`)}
              className="border-red-500/30 text-red-400 h-8" data-testid="detail-cerrar-sesiones-btn">
              <LogOut className="w-3.5 h-3.5 mr-1.5" />Cerrar todas las sesiones
            </Button>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-1.5">
              <History className="w-3.5 h-3.5" />Historial de seguridad
            </p>
            {eventos === null ? (
              <p className="text-xs text-zinc-600">Cargando...</p>
            ) : eventos.length === 0 ? (
              <p className="text-xs text-zinc-600">Sin eventos registrados todavía.</p>
            ) : (
              <div className="relative pl-4 max-h-72 overflow-y-auto" data-testid="detail-historial-seguridad">
                <div className="absolute left-[5px] top-1.5 bottom-1.5 w-px bg-white/10" />
                <div className="space-y-2.5">
                  {eventos.map((e) => (
                    <div key={e.id} className="relative">
                      <span className={`absolute -left-4 top-1 w-[11px] h-[11px] rounded-full border-2 border-[#0a0a0a] ${EVENTO_COLORS[e.evento] || 'bg-zinc-500'}`} />
                      <div className="pl-1">
                        <p className="text-xs text-zinc-400">
                          <span className="text-zinc-600 font-mono">{e.fecha?.slice(0, 16).replace('T', ' ')}</span>
                          {' · '}<span className="text-zinc-300">{e.evento.replace(/_/g, ' ')}</span>
                        </p>
                        <p className="text-[11px] text-zinc-500">{e.detalle} <span className="text-zinc-700">({e.actor})</span></p>
                        {e.enlace && (
                          <button onClick={() => copiar(e.enlace)}
                            className="inline-flex items-center gap-1 mt-0.5 text-[10px] text-sky-400 hover:text-sky-300"
                            data-testid={`copiar-enlace-${e.id}`}>
                            <Copy className="w-2.5 h-2.5" />Copiar enlace
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UserModal({ usuario, roles, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: usuario?.name || '',
    email: usuario?.email || '',
    phone: usuario?.phone || '',
    role: usuario?.role === 'user' ? 'cliente' : (usuario?.role || 'cliente'),
    password: '',
  });
  const [saving, setSaving] = useState(false);
  const [vistaEquipo, setVistaEquipo] = useState(!!usuario?.vista_equipo);
  const esEdicion = !!usuario;

  const guardar = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error('Nombre y email son obligatorios'); return; }
    if (!esEdicion && form.password.length < 8) { toast.error('La contraseña debe tener al menos 8 caracteres'); return; }
    setSaving(true);
    try {
      if (esEdicion) {
        const payload = { name: form.name, phone: form.phone, role: form.role, vista_equipo: vistaEquipo };
        if (form.password) payload.password = form.password;
        await axios.put(`${API_URL}/api/admin/users/${usuario.id}`, payload, { withCredentials: true });
        toast.success('Usuario actualizado');
      } else {
        const r = await axios.post(`${API_URL}/api/admin/users`, form, { withCredentials: true });
        toast.success('Usuario creado. Ya puede acceder con email/contraseña o con Google.');
        if (r.data.verificacion) mostrarResultadoEnvio({ ...r.data.verificacion, message: 'Correo de verificación' });
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="bg-[#0a0a0a] border-white/10 text-white max-w-md" data-testid="user-modal">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] font-light text-xl">
            {esEdicion ? `Editar: ${usuario.name}` : 'Nuevo Usuario'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-zinc-500 mb-1.5 block">Nombre completo *</label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="bg-white/[0.03] border-white/10 text-white" data-testid="user-nombre-input" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1.5 block">Email *{esEdicion && ' (no editable)'}</label>
            <Input value={form.email} disabled={esEdicion} type="email"
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="bg-white/[0.03] border-white/10 text-white disabled:opacity-50" data-testid="user-email-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Teléfono</label>
              <Input value={form.phone || ''} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                className="bg-white/[0.03] border-white/10 text-white" data-testid="user-telefono-input" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Rol</label>
              <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm h-10"
                data-testid="user-rol-select">
                {roles.map((r) => <option key={r} value={r} className="bg-zinc-900">{ROLE_LABELS[r] || r}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1.5 block">
              {esEdicion ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña * (mín. 8 caracteres)'}
            </label>
            <Input type="password" value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="bg-white/[0.03] border-white/10 text-white" data-testid="user-password-input" />
          </div>
          {esEdicion && (usuario.role === 'tecnico' || form.role === 'tecnico') && (
            <label className="flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.03] border border-white/10 cursor-pointer" data-testid="user-vista-equipo-toggle">
              <input type="checkbox" checked={vistaEquipo} onChange={(e) => setVistaEquipo(e.target.checked)}
                className="w-4 h-4 accent-sky-500" />
              <span className="text-xs text-zinc-300">
                <b>Vista de Equipo</b> — este técnico (encargado) verá también los trabajos del resto del equipo en su agenda
              </span>
            </label>
          )}
          <p className="text-[10px] text-zinc-600">
            Al crear la cuenta se envía automáticamente el correo de verificación. El usuario podrá iniciar sesión con
            email/contraseña o con Google (mismo email). Google nunca crea cuentas ni cambia permisos.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} className="border-white/20 text-zinc-300">Cancelar</Button>
            <Button onClick={guardar} disabled={saving} className="bg-white text-black hover:bg-zinc-200" data-testid="user-guardar-btn">
              {saving ? 'Guardando...' : (esEdicion ? 'Guardar cambios' : 'Crear usuario')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState(['admin', 'comercial', 'tecnico', 'cliente']);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [modal, setModal] = useState(null); // 'nuevo' | usuario
  const [detalle, setDetalle] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setUsuarios(r.data.users || []);
      if (r.data.roles) setRoles(r.data.roles);
    } catch (err) {
      toast.error('Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const cambiarEstado = async (u, estado) => {
    const labels = { activo: 'reactivar', suspendido: 'suspender', desactivado: 'desactivar' };
    if (!window.confirm(`¿Seguro que quieres ${labels[estado]} la cuenta de ${u.name}? ${estado !== 'activo' ? 'Perderá el acceso inmediatamente y se cerrarán todas sus sesiones (Google y contraseña). Su historial se conserva.' : 'Recuperará el acceso con su mismo email y permisos.'}`)) return;
    try {
      await axios.put(`${API_URL}/api/admin/users/${u.id}`, { estado }, { withCredentials: true });
      toast.success(`Cuenta de ${u.name} ${estado === 'activo' ? 'reactivada' : estado === 'suspendido' ? 'suspendida' : 'desactivada'}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const filtrados = usuarios.filter((u) =>
    !query || `${u.name} ${u.email} ${ROLE_LABELS[u.role] || u.role}`.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <AdminLayout title="Usuarios" subtitle="Gestión de cuentas, roles, estados y seguridad — la única vía para crear usuarios" onLogout={handleLogout}>
      <div className="space-y-4" data-testid="admin-users-page">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nombre, email o rol..."
              className="pl-9 bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600" data-testid="users-buscar-input" />
          </div>
          <Button onClick={() => setModal('nuevo')} className="bg-white text-black hover:bg-zinc-200" data-testid="users-crear-btn">
            <UserPlus className="w-4 h-4 mr-1.5" />Nuevo usuario
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-zinc-500 py-10 text-center">Cargando usuarios...</p>
        ) : (
          <div className="rounded-xl border border-white/[0.08] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white/[0.03] text-left">
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium">Usuario</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium">Rol</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium">Estado</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium">Verificado</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium">Google</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium">Último acceso</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.05]">
                  {filtrados.map((u) => (
                    <tr key={u.id} className="hover:bg-white/[0.02]" data-testid={`user-row-${u.email}`}>
                      <td className="px-4 py-3">
                        <p className="text-white font-medium">{u.name}{u.id === currentUser?.id && <span className="text-zinc-500 text-xs"> (tú)</span>}</p>
                        <p className="text-xs text-zinc-500">{u.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${ROLE_STYLES[u.role] || ROLE_STYLES.cliente}`}>
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${ESTADO_STYLES[u.estado] || ESTADO_STYLES.activo}`} data-testid={`user-estado-${u.email}`}>
                          {u.estado.charAt(0).toUpperCase() + u.estado.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {u.email_verificado
                          ? <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400"><MailCheck className="w-3 h-3" />Sí</span>
                          : <span className="inline-flex items-center gap-1 text-[10px] text-amber-400" data-testid={`user-sin-verificar-${u.email}`}><MailWarning className="w-3 h-3" />Pendiente</span>}
                      </td>
                      <td className="px-4 py-3">
                        {u.google_linked
                          ? <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400"><ShieldCheck className="w-3 h-3" />Vinculado</span>
                          : <span className="text-[10px] text-zinc-600">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-500">{u.last_login ? u.last_login.slice(0, 16).replace('T', ' ') : 'Nunca'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button variant="outline" size="sm" onClick={() => setDetalle(u)} className="border-white/15 text-zinc-300 h-7 px-2" title="Ficha de seguridad" data-testid={`user-detalle-${u.email}`}>
                            <Eye className="w-3 h-3" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setModal(u)} className="border-white/15 text-zinc-300 h-7 px-2" title="Editar" data-testid={`user-editar-${u.email}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          {u.id !== currentUser?.id && (
                            u.estado === 'activo' ? (
                              <>
                                <Button variant="outline" size="sm" onClick={() => cambiarEstado(u, 'suspendido')}
                                  className="border-amber-500/30 text-amber-400 h-7 px-2" title="Suspender" data-testid={`user-suspender-${u.email}`}>
                                  <Pause className="w-3 h-3" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => cambiarEstado(u, 'desactivado')}
                                  className="border-red-500/30 text-red-400 h-7 px-2" title="Desactivar" data-testid={`user-desactivar-${u.email}`}>
                                  <Ban className="w-3 h-3" />
                                </Button>
                              </>
                            ) : (
                              <Button variant="outline" size="sm" onClick={() => cambiarEstado(u, 'activo')}
                                className="border-emerald-500/30 text-emerald-400 h-7 px-2" title="Reactivar" data-testid={`user-reactivar-${u.email}`}>
                                <Play className="w-3 h-3" />
                              </Button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtrados.length === 0 && <p className="text-sm text-zinc-500 py-8 text-center">Sin resultados</p>}
          </div>
        )}
      </div>

      {modal && (
        <UserModal
          usuario={modal === 'nuevo' ? null : modal}
          roles={roles}
          onClose={() => setModal(null)}
          onSaved={fetchUsers}
        />
      )}
      {detalle && (
        <UserDetailModal
          usuario={detalle}
          onClose={() => setDetalle(null)}
          onChanged={fetchUsers}
        />
      )}
    </AdminLayout>
  );
}
